// Свайп строки списка чатов (В5, 1.27.4): влево и вправо — действия над строкой.
//
// use:rowSwipe={{ left: true, right: true, leaveOnLeft: true, onCommit: (direction) => … }}
//
// Почему не actions/swipe.js (свайпы экранов). Владелец на iPhone: «сдвиг отменяет свайп,
// вправо почти невозможно, не плавно». Там ось решалась на 12 px при перевесе ×1,5, а
// вертикаль отдавалась прокрутке (touch-action: pan-y): дуга большого пальца уходила в
// «вертикальный» жест, а малейший сдвиг вверх-вниз запускал прокрутку, iOS слал
// pointercancel, и жест гас. Здесь — touch-события: ось решается на первых 8 px простым
// «вбок больше, чем вверх-вниз», после этого touchmove гасится (preventDefault) — список
// не прокручивается, и дрожание пальца жест уже не отменяет. Строка двигается прямо в
// DOM (transform за кадр), без перерисовки списка из сотен строк. Подпись действия
// показывает CSS по data-swipe у родителя строки. Мышь (десктоп, e2e) — Pointer Events.

const AXIS_SLOP_PX = 8;
const MIN_COMMIT_PX = 80;
const COMMIT_RATIO = 0.35;
const VELOCITY_PX_PER_MS = 0.35;
const MIN_VELOCITY_DX = 40;
const RESISTANCE = 0.25;
const VELOCITY_WINDOW_MS = 100;
const CLICK_SUPPRESS_MS = 350;
const SETTLE_MS = 220;
const IGNORE_SELECTOR = 'button, input, textarea, select, [data-no-swipe]';

function now() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function nextFrame(callback) {
  return typeof requestAnimationFrame === 'function' ? requestAnimationFrame(callback) : setTimeout(callback, 16);
}

export function rowSwipe(node, options = {}) {
  let opts = options || {};
  let tracking = false;
  let axis = null;
  let startX = 0;
  let startY = 0;
  let dx = 0;
  let samples = [];
  let frame = null;
  let pointerId = null;
  let suppressClickUntil = 0;
  let settleTimer = null;

  const host = () => node.parentElement;

  function allowed(direction) {
    return direction === 'left' ? opts.left !== false : opts.right !== false;
  }

  function commitDistance() {
    const width = node.getBoundingClientRect?.().width || node.offsetWidth || 0;
    return Math.max(MIN_COMMIT_PX, width * COMMIT_RATIO);
  }

  function directionOf(value) {
    if (value < 0) return 'left';
    if (value > 0) return 'right';
    return '';
  }

  function render() {
    frame = null;
    node.style.transform = dx ? `translateX(${dx}px)` : '';
    const parent = host();
    if (parent) {
      const direction = directionOf(dx);
      parent.dataset.swipe = direction && allowed(direction) ? direction : '';
      parent.dataset.swipeArmed = direction && allowed(direction) && Math.abs(dx) >= commitDistance() ? '1' : '';
    }
  }

  function scheduleRender() {
    if (frame === null) {
      frame = nextFrame(render);
    }
  }

  function clearHost() {
    const parent = host();
    if (parent) {
      parent.dataset.swipe = '';
      parent.dataset.swipeArmed = '';
    }
  }

  function begin(x, y) {
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    tracking = true;
    axis = null;
    startX = x;
    startY = y;
    dx = 0;
    samples = [{ t: now(), x }];
  }

  // true — жест горизонтальный и событие надо погасить (прокрутку не отдаём).
  function move(x, y) {
    if (!tracking) {
      return false;
    }
    const rawDx = x - startX;
    const rawDy = y - startY;
    if (axis === null) {
      if (Math.abs(rawDx) < AXIS_SLOP_PX && Math.abs(rawDy) < AXIS_SLOP_PX) {
        return false;
      }
      axis = Math.abs(rawDx) > Math.abs(rawDy) ? 'x' : 'y';
      if (axis === 'y') {
        tracking = false;
        return false;
      }
      // Во время жеста строка идёт за пальцем без CSS-перехода.
      node.style.transition = 'none';
    }
    const t = now();
    samples.push({ t, x });
    while (samples.length > 2 && t - samples[0].t > VELOCITY_WINDOW_MS) {
      samples.shift();
    }
    const direction = directionOf(rawDx);
    dx = direction && !allowed(direction) ? rawDx * RESISTANCE : rawDx;
    scheduleRender();
    return true;
  }

  function velocity() {
    if (samples.length < 2) {
      return 0;
    }
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = last.t - first.t;
    return dt > 0 ? (last.x - first.x) / dt : 0;
  }

  function end(cancelled) {
    if (!tracking) {
      return;
    }
    tracking = false;
    if (axis !== 'x') {
      return;
    }
    const direction = directionOf(dx);
    const speed = cancelled ? 0 : velocity();
    const committed = !cancelled && Boolean(direction) && allowed(direction) && (
      Math.abs(dx) >= commitDistance()
      || (Math.sign(speed) === Math.sign(dx) && Math.abs(speed) >= VELOCITY_PX_PER_MS && Math.abs(dx) >= MIN_VELOCITY_DX)
    );
    suppressClickUntil = now() + CLICK_SUPPRESS_MS;
    if (frame !== null) {
      render();
    }
    // Отпустили — строка едет по CSS-переходу: уезжает влево (архив) или возвращается.
    node.style.transition = '';
    const leaving = committed && direction === 'left' && opts.leaveOnLeft;
    node.style.transform = leaving ? 'translateX(-100%)' : '';
    dx = 0;
    if (!leaving) {
      settleTimer = setTimeout(() => {
        settleTimer = null;
        clearHost();
      }, SETTLE_MS);
    }
    if (!committed) {
      return;
    }
    Promise.resolve()
      .then(() => opts.onCommit?.(direction))
      .catch(() => {})
      .finally(() => {
        // Строку не убрали (отмена, ошибка, закрепление) — возвращаем на место.
        node.style.transform = '';
        clearHost();
      });
  }

  function ignored(target) {
    return Boolean(target && typeof target.closest === 'function' && target.closest(IGNORE_SELECTOR));
  }

  // ── Касание ──
  function onTouchStart(event) {
    if (event.touches.length !== 1 || ignored(event.target)) {
      tracking = false;
      return;
    }
    const touch = event.touches[0];
    begin(touch.clientX, touch.clientY);
  }

  function onTouchMove(event) {
    if (!tracking || event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    if (move(touch.clientX, touch.clientY) && event.cancelable) {
      event.preventDefault();
    }
  }

  function onTouchEnd() {
    end(false);
  }

  function onTouchCancel() {
    end(true);
  }

  // ── Мышь (десктоп, e2e): касание Pointer Events не трогаем — его ведут touch-события ──
  function onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || ignored(event.target)) {
      return;
    }
    pointerId = event.pointerId ?? null;
    begin(event.clientX, event.clientY);
  }

  function onPointerMove(event) {
    if (event.pointerType !== 'mouse' || !tracking) {
      return;
    }
    const wasUndecided = axis === null;
    move(event.clientX, event.clientY);
    if (wasUndecided && axis === 'x' && pointerId !== null) {
      try {
        node.setPointerCapture?.(pointerId);
      } catch {
        // без захвата жест работает, пока курсор над строкой
      }
    }
  }

  function onPointerUp(event) {
    if (event.pointerType !== 'mouse') {
      return;
    }
    pointerId = null;
    end(false);
  }

  function onPointerCancel(event) {
    if (event.pointerType !== 'mouse') {
      return;
    }
    pointerId = null;
    end(true);
  }

  function onClickCapture(event) {
    if (now() < suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  node.addEventListener('touchstart', onTouchStart, { passive: true });
  node.addEventListener('touchmove', onTouchMove, { passive: false });
  node.addEventListener('touchend', onTouchEnd);
  node.addEventListener('touchcancel', onTouchCancel);
  node.addEventListener('pointerdown', onPointerDown);
  node.addEventListener('pointermove', onPointerMove);
  node.addEventListener('pointerup', onPointerUp);
  node.addEventListener('pointercancel', onPointerCancel);
  node.addEventListener('click', onClickCapture, true);

  return {
    update(next = {}) {
      opts = next || {};
    },
    destroy() {
      if (settleTimer) {
        clearTimeout(settleTimer);
      }
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchCancel);
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerCancel);
      node.removeEventListener('click', onClickCapture, true);
    },
  };
}
